// Compatibility layer: maps the Base44 entity API (.list/.filter/.get/.create/.update/.delete)
// onto the existing CITIDAN REST backend via the shared axios client. The backend is never modified.
import api from "../../lib/api";

export function toArr(d) {
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object") {
    for (const k of ["items", "results", "data", "rows"]) {
      if (Array.isArray(d[k])) return d[k];
    }
  }
  return [];
}

// Client-side filter. Base44 screens often filter by fields CITIDAN docs don't have
// (e.g. created_by). If none of the requested fields exist on any record, we return all
// records rather than an empty list, so real data still shows.
export function clientFilter(records, query) {
  const keys = Object.keys(query || {});
  if (!keys.length) return records;
  const active = keys.filter((k) => records.some((r) => r && r[k] !== undefined));
  if (!active.length) return records;
  return records.filter((r) => active.every((k) => r[k] === query[k]));
}

export function makeEntity(endpoint) {
  return {
    async list() {
      const { data } = await api.get(endpoint);
      return toArr(data);
    },
    async filter(query) {
      const { data } = await api.get(endpoint);
      return clientFilter(toArr(data), query);
    },
    async get(id) {
      const { data } = await api.get(`${endpoint}/${id}`);
      return data;
    },
    async create(payload) {
      const { data } = await api.post(endpoint, payload);
      return data;
    },
    async bulkCreate(list) {
      const out = [];
      for (const p of list || []) {
        const { data } = await api.post(endpoint, p);
        out.push(data);
      }
      return out;
    },
    async update(id, payload) {
      const { data } = await api.put(`${endpoint}/${id}`, payload);
      return data;
    },
    async delete(id) {
      await api.delete(`${endpoint}/${id}`);
      return true;
    },
  };
}

// For entities that have no CITIDAN endpoint: reads return empty (screens render a clean
// empty / "not connected" state); writes reject with a clear, catchable message.
export function makeStub(name) {
  const fail = () =>
    Promise.reject(
      Object.assign(new Error(`${name} is not connected to the CITIDAN backend yet.`), {
        notConnected: true,
      })
    );
  return {
    __notConnected: true,
    async list() { return []; },
    async filter() { return []; },
    async get() { return null; },
    create: fail,
    update: fail,
    delete: fail,
    bulkCreate: fail,
  };
}
